'use client';

import React, { JSX, useEffect, useRef, useState } from 'react';
import { buildFacet, Facet, FacetState, FacetSortCriterion } from '@coveo/headless';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { SvgIcon } from 'helpers/SvgIcon';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';

type FacetGroupProps = {
  fields: string[];
  labels?: Record<string, string>;
  numberOfValues?: number;
  sortCriteria?: FacetSortCriterion;
  forceAccordionOnDesktop?: boolean;
  hideHeader?: boolean;
  isIndependentFacet?: boolean;
};

export type BuiltFacet = {
  facet: Facet;
  state: FacetState;
  field: string;
};

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isDesktop;
};

/*
Provides a group of facets for Coveo search
Variant: the flag isIndependentFacet provides the ability to display a facet group that links to the AET Blog results page
 */
const FacetGroup = ({
  fields,
  labels = {},
  numberOfValues = 8,
  sortCriteria = 'occurrences',
  forceAccordionOnDesktop = false,
  hideHeader = false,
  isIndependentFacet = false,
}: FacetGroupProps): JSX.Element | null => {
  const engine = useCoveoEngine();
  const isDesktop = useIsDesktop();
  const useAccordionLayout = !isDesktop || forceAccordionOnDesktop; // derive layout mode

  const [facets, setFacets] = useState<BuiltFacet[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [openFacet, setOpenFacet] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState<Record<string, boolean>>({});
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { getDictionaryValue } = useDictionary();
  const clearNumFilterLabel = getDictionaryValue('ClearNumFilters');
  const siteSettings = useSiteSettings()?.fields;
  const blogSearchPageLink = siteSettings?.blogSearchPage?.value?.href;

  useEffect(() => {
    if (!engine || fields.length === 0) return;

    const createdFacets: BuiltFacet[] = fields.map((field) => {
      const facet = buildFacet(engine, {
        options: {
          field,
          facetId: field,
          numberOfValues, // Use the provided numberOfValues for initial display
          sortCriteria,
        },
      });

      return {
        facet,
        state: facet.state,
        field,
      };
    });

    const unsubscribes = createdFacets.map((f, index) =>
      f.facet.subscribe(() => {
        setFacets((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            state: f.facet.state,
          };
          return updated;
        });
      })
    );

    setFacets(createdFacets);
    return () => unsubscribes.forEach((unsub) => unsub());
  }, [engine, fields, numberOfValues, sortCriteria]);

  const handleInputChange = async (field: string, value: string) => {
    const currentFacet = facets.find((f) => f.field === field);
    if (!currentFacet) return;

    setIsSearching((prev) => ({
      ...prev,
      [field]: true,
    }));

    try {
      if (value.trim() === '') {
        // Reset to initial state when search is cleared
        currentFacet.facet.showLessValues();
        setFilters((prev) => ({
          ...prev,
          [field]: '',
        }));
      } else {
        // When searching, temporarily show more values to search through more items
        await currentFacet.facet.showMoreValues();
        // Keep showing more values until we can't anymore or have a good number of matches
        let attempts = 0;
        const maxAttempts = 5; // Prevent infinite loops
        while (
          attempts < maxAttempts &&
          currentFacet.facet.state.canShowMoreValues &&
          currentFacet.facet.state.values.filter((v) =>
            v.value.toLowerCase().includes(value.toLowerCase())
          ).length < 10
        ) {
          await currentFacet.facet.showMoreValues();
          attempts++;
        }

        // Update the filter after loading more values
        setFilters((prev) => ({
          ...prev,
          [field]: value.toLowerCase(),
        }));
      }
    } finally {
      setIsSearching((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  // Close dropdown on outside click (only when dropdown layout is used)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (useAccordionLayout || !openFacet) return;

      const ref = dropdownRefs.current[openFacet];
      if (ref && !ref.contains(e.target as Node)) {
        setOpenFacet(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [useAccordionLayout, openFacet]);

  if (!facets.length) return null;

  return (
    <div
      className={`facet-group-wrapper py-[24px] ${hideHeader ? 'border-none' : 'lg:border-b'} lg:border-gray-50 ${
        !useAccordionLayout ? 'flex flex-wrap gap-[48px]' : 'block'
      }`}
    >
      {useAccordionLayout && !hideHeader && (
        <div className="py-8 flex items-center justify-between border-b-2 border-gray-50">
          <div className="text-[30px] leading-[36px] font-bold text-black font-centuryGothic">
            {getDictionaryValue('Filters') || 'Filters'}
          </div>

          <ButtonWrapper
            ctaVariant="hyperlink"
            onClick={() => {
              facets.forEach(({ facet }) => facet.deselectAll());
            }}
            className="text-sm text-blue-600 hover:underline items-center ml-2"
          >
            {getDictionaryValue('ClearAllFilters') || 'Clear all filters'}
          </ButtonWrapper>
        </div>
      )}
      {facets.map(({ facet, state, field }) => {
        const label = labels[field] || field;
        const filterText = filters[field] || '';
        const values = filterText
          ? state.values.filter((val) => val.value.toLowerCase().includes(filterText.toLowerCase()))
          : state.values;

        const isOpen = openFacet === field;

        const Content = (
          <>
            {state.hasActiveValues && !useAccordionLayout && (
              <div className="mb-2 text-left lg:px-10 px-0">
                <ButtonWrapper
                  onClick={() => facet.deselectAll()}
                  className="text-sm text-blue-600 font-semibold hover:underline"
                  ctaIcon="close"
                  ctaIconAlignment="left"
                  ctaVariant="tertiary"
                >
                  {clearNumFilterLabel.replace(
                    '{num}',
                    state.values.filter((v) => v.state === 'selected').length.toString()
                  )}
                  {state.values.filter((v) => v.state === 'selected').length > 1 ? 's' : ''}
                </ButtonWrapper>
              </div>
            )}
            {!hideHeader && (
              <div className="lg:px-10 px-0">
                <div className="relative">
                  <input
                    id={label}
                    type="text"
                    placeholder={`Search the list`}
                    value={filterText}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    className="border-2 border-gray-100 rounded-[8px] px-12 py-7 w-full text-sm font-nunitoSans font-normal !text-20 text-gray-700 mb-4"
                  />
                  {isSearching[field] && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <ul className="lg-mt-0 mt-6">
              {values.map((val) => {
                const constructFacetUrl = () => {
                  if (!isIndependentFacet || !blogSearchPageLink) return '';
                  const baseLink = blogSearchPageLink as string;
                  const url = new URL(baseLink, window.location.origin);
                  const encodedValue = encodeURIComponent(val.value);
                  return `${url.pathname}${url.search}#f-${field}=${encodedValue}`;
                };

                return (
                  <li key={val.value} className="py-6 lg:px-10 px-0">
                    {isIndependentFacet ? (
                      <a href={constructFacetUrl()}>
                        <label className="flex items-start space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={val.state === 'selected'}
                              onChange={() => facet.toggleSelect(val)}
                              className="peer appearance-none w-12 h-12 border-2 border-gray-400 rounded-[4px] 
                       mr-[15px] mt-[2px] checked:bg-blue-base checked:border-blue-base 
                       focus:outline-none"
                            />
                            <div
                              className="pointer-events-none absolute top-[6px] left-[9px] w-[6px] h-[12px]
                       border-b-2 border-r-2 border-white rotate-45 scale-0 
                       peer-checked:scale-100 transition-transform duration-150 ease-in-out"
                            />
                          </div>
                          <div className="font-nunitoSans text-16 leading-[24px] font-medium">
                            <span className="text-gray-900">{val.value}</span>{' '}
                            <span className="text-gray-400">({val.numberOfResults})</span>
                          </div>
                        </label>
                      </a>
                    ) : (
                      <label className="flex items-start space-x-2 cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={val.state === 'selected'}
                            onChange={() => facet.toggleSelect(val)}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                facet.toggleSelect(val);
                              }
                            }}
                            className="peer appearance-none w-12 h-12 border-2 border-gray-400 rounded-[4px] 
                       mr-[15px] mt-[2px] checked:bg-blue-base checked:border-blue-base 
                      focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <div
                            className="pointer-events-none absolute top-[6px] left-[9px] w-[6px] h-[12px]
                       border-b-2 border-r-2 border-white rotate-45 scale-0 
                       peer-checked:scale-100 transition-transform duration-150 ease-in-out"
                          />
                        </div>
                        <div className="font-nunitoSans text-16 leading-[24px] font-medium">
                          <span className="text-gray-900">{val.value}</span>{' '}
                          <span className="text-gray-400">({val.numberOfResults})</span>
                        </div>
                      </label>
                    )}
                  </li>
                );
              })}
              {values.length === 0 && (
                <li className="text-sm text-gray-500 italic lg:px-10 px-0">No matches found</li>
              )}
            </ul>
            {filterText === '' && !isSearching[field] && (
              <div className="mt-4 [&_button]:justify-start">
                {state.canShowMoreValues && (
                  <ButtonWrapper
                    ctaVariant="tertiary"
                    className="text-blue-600 font-semibold !no-underline text-sm flex lg:px-10 px-0 hover:bg-blue-50 !rounded-none w-full"
                    onClick={() => facet.showMoreValues()}
                  >
                    {getDictionaryValue('ShowMore')}
                    <SvgIcon
                      icon={'plus'}
                      className="ml-4 w-[22px] h-[22px] mb-1 [&>path]:fill-current"
                      aria-label={'Expand list'}
                    />
                  </ButtonWrapper>
                )}
                {state.canShowLessValues && (
                  <ButtonWrapper
                    ctaVariant="tertiary"
                    className="text-blue-600 font-semibold !no-underline text-sm flex lg:px-10 px-0 hover:bg-blue-50 !rounded-none w-full"
                    onClick={() => facet.showLessValues()}
                  >
                    {getDictionaryValue('ShowLess')}
                    <SvgIcon
                      icon={'minus'}
                      className="ml-4 w-[22px] h-[22px] mb-1 [&>path]:fill-current"
                      aria-label={'Collapse list'}
                    />
                  </ButtonWrapper>
                )}
              </div>
            )}
          </>
        );

        return (
          <div
            key={field}
            className={`${!useAccordionLayout ? 'relative w-auto' : ''} rounded-lg bg-white`}
            ref={(el) => {
              dropdownRefs.current[field] = el;
            }}
          >
            {!useAccordionLayout ? (
              <>
                <button
                  type="button"
                  onClick={() => setOpenFacet((prev) => (prev === field ? null : field))}
                  className={`flex justify-between items-center w-full px-4 py-5 font-bold text-lg text-left cursor-pointer rounded-[8px] hover:bg-blue-50 group ${isOpen ? 'bg-blue-50' : 'bg-transparent'}`}
                >
                  <span
                    className={`font-nunitoSans text-16 leading-[25px] font-medium text-gray-900 mr-4 group-hover:underline ${isOpen ? 'underline' : 'no-underline'}`}
                  >
                    {label}
                  </span>
                  <span className="-mt-1">
                    <SvgIcon
                      className={'duration-200 h-auto trasition !w-xs [&>path]:fill-blue-base'}
                      fill="none"
                      icon={`${isOpen ? 'chevron-up' : 'chevron-down'}`}
                      size="md"
                      viewBox="0 0 24 24"
                    />
                  </span>
                </button>
                {isOpen && (
                  <div className="absolute left-0 z-20 mt-2 w-full min-w-[400px] max-h-[400px] overflow-y-auto shadow-default bg-white rounded-[10px] border border-gray-50 py-10">
                    {Content}
                  </div>
                )}
              </>
            ) : (
              <details open={openFacet === field} className="w-full border-b-2 border-gray-50">
                <summary
                  className="list-none font-bold text-lg py-12 px-6 flex justify-between items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenFacet((prev) => (prev === field ? null : field));
                  }}
                >
                  <span className="font-nunitoSans lg:text-16 text-18 leading-[25px] lg:font-semibold text-gray-900 mr-4">
                    {label}
                  </span>
                  <span className="-mt-1">
                    <SvgIcon
                      className={'duration-200 h-auto trasition !w-xs [&>path]:fill-blue-base'}
                      fill="none"
                      icon={`${openFacet === field ? 'chevron-up' : 'chevron-down'}`}
                      size="md"
                      viewBox="0 0 24 24"
                    />
                  </span>
                </summary>
                <div className="p-4">{Content}</div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FacetGroup;
